import { useMemo } from 'react';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { Region } from '@/creditflowresearch/domain/Region';
import type { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { PATH_ONLY_GRANT_CONTEXT } from '@/creditflowresearch/application/User/access/grants/pagePatterns';

export interface IRegionFilter {
  id: string,
  abbreviation: string,
  queryName?: string,
  disabled?: boolean,
  tooltip?: string | React.ReactNode,
  displayName?: string,
  name?: string,
  shortDisplayName?: string,
}

export const useCreateRegionFilters = (): IRegionFilter[] => {
  const { regions: regionsList } = useHeaderMenuContext();
  const {
    regions: accessibleRegionsIds,
    plan,
  } = useUserPlanContext();
  // TODO: remove when the European market opening period ends
  const isOpenAccessPage = useAccessGrant(PATH_ONLY_GRANT_CONTEXT);

  return useMemo<IRegionFilter[]>(() => {
    return regionsList
      .map((regionData: IRegion) => {
        const region = Region.from(regionData.id);
        if (!region) return null;

        const disabled = !isOpenAccessPage
          && (!accessibleRegionsIds.includes(region.getId()) || !UserPlan.isSatisfiedPremiumPlan(plan));

        return {
          id: region.getId(),
          abbreviation: region.getAbbreviation() as string,
          queryName: region.getAbbreviation() as string,
          disabled,
          tooltip: `Includes deals issued with currencies or issuers domiciled in ${ region.getName() }`,
          name: region.getName(),
          displayName: regionData.displayName,
          shortDisplayName: regionData.shortDisplayName,
        };
      })
      .filter(Boolean) as IRegionFilter[];
  }, [regionsList, accessibleRegionsIds, plan, isOpenAccessPage]);
};
